import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './Quality.css';
import qualityAPI from '../../api/quality';
import authAPI from '../../api/auth';
import { hasQualityPermission } from '../../utils/permissions';

function Tablica({ user, projects: initialProjects = [], users: allUsers = [], openAlertId = null, onOpenAlertHandled = null }) {
  const [alerts, setAlerts] = useState([]);
  const openAlertIdHandledRef = useRef(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all, open, in_progress, resolved, closed
  const [filterPriority, setFilterPriority] = useState('all'); // all, low, medium, high, critical
  const [filterClient, setFilterClient] = useState('all'); // all, or specific client name
  const [sortBy, setSortBy] = useState('createdAt'); // createdAt, priority, dueDate
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [selectedImage, setSelectedImage] = useState(null); // { src, name, alertId, imageIndex } dla modala
  const [selectedAlert, setSelectedAlert] = useState(null); // Alert do wyświetlenia w modalu
  const [selectedAlertIndex, setSelectedAlertIndex] = useState(-1); // Indeks wybranego alertu w filteredAndSortedAlerts
  const [editingAlert, setEditingAlert] = useState(null); // Alert do edycji
  const [editFormData, setEditFormData] = useState(null); // Dane formularza edycji
  const [editImages, setEditImages] = useState([]); // Zdjęcia w formularzu edycji
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Modal potwierdzenia usunięcia
  const [alertToDelete, setAlertToDelete] = useState(null); // Alert do usunięcia
  const [showAddForm, setShowAddForm] = useState(false); // Przełącznik widoku formularza dodawania
  const [projects, setProjects] = useState(initialProjects); // Projekty dla formularza

  // Aktualizuj projekty z props
  useEffect(() => {
    setProjects(initialProjects);
    const handleProjectsUpdated = (event) => {
      if (event.detail) {
        setProjects(event.detail);
      } else {
        setProjects(initialProjects);
      }
    };
    window.addEventListener('projects-updated', handleProjectsUpdated);
    return () => {
      window.removeEventListener('projects-updated', handleProjectsUpdated);
    };
  }, [initialProjects]);

  const priorities = {
    low: { label: 'Niski', color: '#3b82f6' },
    medium: { label: 'Średni', color: '#f59e0b' },
    high: { label: 'Wysoki', color: '#ef4444' },
    critical: { label: 'Krytyczny', color: '#dc2626' }
  };

  const statuses = {
    open: { label: 'Otwarty', color: '#3b82f6' },
    in_progress: { label: 'W trakcie', color: '#f59e0b' },
    resolved: { label: 'Rozwiązany', color: '#10b981' },
    closed: { label: 'Zamknięty', color: '#6b7280' }
  };

  // Wczytaj alerty z API
  const loadAlerts = async () => {
    try {
      const alertsData = await qualityAPI.getAll();
      setAlerts(alertsData);
    } catch (error) {
      console.error('Błąd ładowania alertów:', error);
      setAlerts([]);
    }
  };

  // Funkcja do zmiany statusu alertu
  const updateAlertStatus = async (alertId, newStatus) => {
    try {
      await qualityAPI.update(alertId, { status: newStatus });
      await loadAlerts();
      window.dispatchEvent(new CustomEvent('quality-alerts-updated'));
    } catch (error) {
      console.error('Błąd aktualizacji statusu alertu:', error);
      alert(`Błąd podczas aktualizacji: ${error.message}`);
    }
  };

  // Funkcja do otwarcia modala potwierdzenia usunięcia
  const handleDeleteClick = (alert, event) => {
    event.stopPropagation(); // Zapobiegaj otwieraniu modala szczegółów
    setAlertToDelete(alert);
    setShowDeleteModal(true);
  };

  // Funkcja do usunięcia alertu
  const handleDeleteConfirm = async () => {
    if (!alertToDelete) return;

    try {
      await qualityAPI.delete(alertToDelete.id);
      await loadAlerts();
      window.dispatchEvent(new CustomEvent('quality-alerts-updated'));
      
      // Zamknij modal i wyczyść stan
      setShowDeleteModal(false);
      setAlertToDelete(null);
      
      // Jeśli usunięty alert był otwarty w modalu, zamknij modal
      if (selectedAlert && selectedAlert.id === alertToDelete.id) {
        setSelectedAlert(null);
        setSelectedAlertIndex(-1);
      }
    } catch (error) {
      console.error('Błąd usuwania alertu:', error);
      alert(`Błąd podczas usuwania: ${error.message}`);
    }
  };

  // Funkcja do anulowania usunięcia
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setAlertToDelete(null);
  };

  // Funkcja do aktualizacji strzałek na zdjęciu
  const updateImageArrows = async (alertId, imageIndex, arrows) => {
    try {
      const alert = alerts.find(a => a.id === alertId);
      if (!alert || !alert.images || !alert.images[imageIndex]) return;

      const updatedImages = [...alert.images];
      updatedImages[imageIndex] = {
        ...updatedImages[imageIndex],
        arrows: arrows || []
      };

      await qualityAPI.update(alertId, { images: updatedImages });
      await loadAlerts();
      
      // Aktualizuj selectedAlert, jeśli jest otwarty
      if (selectedAlert && selectedAlert.id === alertId) {
        const updatedAlerts = await qualityAPI.getAll();
        const updatedAlert = updatedAlerts.find(a => a.id === alertId);
        if (updatedAlert) {
          setSelectedAlert(updatedAlert);
        }
      }
      
      // Aktualizuj selectedImage, jeśli jest otwarty
      if (selectedImage && selectedImage.alertId === alertId && selectedImage.imageIndex === imageIndex) {
        const updatedAlerts = await qualityAPI.getAll();
        const updatedAlert = updatedAlerts.find(a => a.id === alertId);
        if (updatedAlert) {
          const updatedImage = updatedAlert.images?.[imageIndex];
          if (updatedImage) {
            setSelectedImage({
              ...selectedImage,
              arrows: updatedImage.arrows || [],
              allImages: updatedAlert.images || []
            });
          }
        }
      }
      
      window.dispatchEvent(new CustomEvent('quality-alerts-updated'));
    } catch (error) {
      console.error('Błąd aktualizacji strzałek:', error);
      alert(`Błąd podczas aktualizacji: ${error.message}`);
    }
  };

  // Funkcja do dodania nowej strzałki
  const addImageArrow = (alertId, imageIndex, x, y) => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert || !alert.images || !alert.images[imageIndex]) return;

    const currentArrows = alert.images[imageIndex].arrows || [];
    const newArrow = {
      id: Date.now() + Math.random(), // Unikalne ID
      x, // Pozycja grotu strzałki (miejsce kliknięcia)
      y,
      angle: 0 // Domyślny kąt (strzałka wskazuje w dół)
    };

    const updatedArrows = [...currentArrows, newArrow];
    updateImageArrows(alertId, imageIndex, updatedArrows);
  };

  // Funkcja do usunięcia strzałki
  const removeImageArrow = (alertId, imageIndex, arrowId) => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert || !alert.images || !alert.images[imageIndex]) return;

    const currentArrows = alert.images[imageIndex].arrows || [];
    const updatedArrows = currentArrows.filter(arrow => arrow.id !== arrowId);
    updateImageArrows(alertId, imageIndex, updatedArrows);
  };

  // Funkcja do aktualizacji kąta strzałki
  const updateArrowAngle = (alertId, imageIndex, arrowId, newAngle) => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert || !alert.images || !alert.images[imageIndex]) return;

    const currentArrows = alert.images[imageIndex].arrows || [];
    const updatedArrows = currentArrows.map(arrow => 
      arrow.id === arrowId ? { ...arrow, angle: newAngle } : arrow
    );
    updateImageArrows(alertId, imageIndex, updatedArrows);
  };

  // Funkcja do otwarcia edycji alertu
  const openEditAlert = (alert) => {
    setEditingAlert(alert);
    setEditFormData({
      title: alert.title || '',
      description: alert.description || '',
      priority: alert.priority || 'medium',
      status: alert.status || 'open',
      category: alert.category || '',
      assignedTo: alert.assignedTo || '',
      dueDate: alert.dueDate || '',
      project: alert.project || '',
      errorDate: alert.errorDate || '',
      correctiveActions: alert.correctiveActions || ''
    });
    setEditImages(alert.images ? alert.images.map(img => ({ 
      ...img, 
      printIncluded: img.printIncluded !== undefined ? img.printIncluded : true 
    })) : []);
  };

  // Funkcja do zamknięcia edycji
  const closeEditAlert = () => {
    setEditingAlert(null);
    setEditFormData(null);
    setEditImages([]);
  };

  // Funkcja do aktualizacji alertu
  const updateAlert = async () => {
    if (!editingAlert || !editFormData) return;

    if (!editFormData.title.trim()) {
      alert('Wprowadź tytuł alertu');
      return;
    }

    try {
      const updateData = {
        ...editFormData,
        images: editImages
      };

      await qualityAPI.update(editingAlert.id, updateData);
      await loadAlerts();
      
      // Jeśli alert był otwarty w modalu, zaktualizuj go
      if (selectedAlert && selectedAlert.id === editingAlert.id) {
        const updatedAlerts = await qualityAPI.getAll();
        const updatedAlert = updatedAlerts.find(a => a.id === editingAlert.id);
        if (updatedAlert) {
          setSelectedAlert(updatedAlert);
        }
      }
      
      window.dispatchEvent(new CustomEvent('quality-alerts-updated'));
      closeEditAlert();
    } catch (error) {
      console.error('Błąd aktualizacji alertu:', error);
      alert(`Błąd podczas aktualizacji: ${error.message}`);
    }
  };


  // Funkcje do zarządzania zdjęciami w edycji
  const handleAddImageEdit = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (editImages.length >= 5) {
        alert('Możesz dodać maksymalnie 5 zdjęć');
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`Plik jest za duży (max 5MB): ${file.name}`);
        return;
      }

      // Konwertuj plik na base64 z kompresją
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        
        // Kompresuj zdjęcie jeśli jest za duże
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;
          let width = img.width;
          let height = img.height;

          // Oblicz nowe wymiary zachowując proporcje
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = (height * MAX_WIDTH) / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = (width * MAX_HEIGHT) / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Konwertuj do base64 z kompresją (jakość 0.7)
          const compressedData = canvas.toDataURL('image/jpeg', 0.7);
          
          const newImage = {
            name: file.name,
            data: compressedData,
            size: compressedData.length,
            comment: '',
            status: null,
            arrow: null, // { x, y } - pozycja strzałki w procentach (0-100)
            printIncluded: true // Domyślnie zdjęcie będzie drukowane
          };
          setEditImages(prev => [...prev, newImage]);
        };
        img.onerror = () => {
          // Jeśli błąd kompresji, użyj oryginalnego obrazu (ale tylko jeśli jest mały)
          if (result.length < 500000) { // Tylko jeśli mniejsze niż ~500KB
            const newImage = {
              name: file.name,
              data: result,
              size: file.size,
              comment: '',
              status: null,
              arrow: null
            };
            setEditImages(prev => [...prev, newImage]);
          } else {
            alert('Zdjęcie jest za duże. Spróbuj zmniejszyć rozmiar przed dodaniem.');
          }
        };
        img.src = result;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const removeImageEdit = (index) => {
    setEditImages(prev => prev.filter((_, i) => i !== index));
  };

  const updateImageCommentEdit = (index, comment) => {
    setEditImages(prev => prev.map((img, i) => 
      i === index ? { ...img, comment } : img
    ));
  };

  const updateImageStatusEdit = (index, status) => {
    setEditImages(prev => prev.map((img, i) => 
      i === index ? { ...img, status } : img
    ));
  };

  const updateImagePrintIncluded = (index, printIncluded) => {
    setEditImages(prev => prev.map((img, i) => 
      i === index ? { ...img, printIncluded } : img
    ));
  };

  const handleImageClickEdit = (index, event) => {
    const imgElement = event.currentTarget.querySelector('img');
    if (!imgElement) return;
    
    const rect = imgElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    // Sprawdź czy kliknięto w istniejącą strzałkę (usuń ją) lub dodaj nową
    const currentImage = editImages[index];
    if (currentImage.arrow) {
      // Sprawdź czy kliknięto w obszar strzałki (w promieniu ~5% od pozycji)
      const arrowX = currentImage.arrow.x;
      const arrowY = currentImage.arrow.y;
      const distance = Math.sqrt(Math.pow(x - arrowX, 2) + Math.pow(y - arrowY, 2));
      
      if (distance < 5) {
        // Usuń strzałkę
        setEditImages(prev => prev.map((img, i) => 
          i === index ? { ...img, arrow: null } : img
        ));
        return;
      }
    }
    
    // Dodaj/aktualizuj strzałkę
    setEditImages(prev => prev.map((img, i) => 
      i === index ? { ...img, arrow: { x, y } } : img
    ));
  };

  const removeImageArrowEdit = (index, event) => {
    event.stopPropagation();
    setEditImages(prev => prev.map((img, i) => 
      i === index ? { ...img, arrow: null } : img
    ));
  };

  useEffect(() => {
    loadAlerts();

    // Nasłuchuj aktualizacji alertów
    const handleUpdate = () => {
      loadAlerts();
    };

    window.addEventListener('quality-alerts-updated', handleUpdate);
    return () => {
      window.removeEventListener('quality-alerts-updated', handleUpdate);
    };
  }, []);

  // Pobierz listę dostępnych klientów (projektów)
  const [availableClients, setAvailableClients] = useState([]);

  useEffect(() => {
    const loadClients = () => {
      try {
        // Użyj projektów z props
        const projectsList = projects || [];
        
        // Pobierz unikalne klienty z alertów (aby uwzględnić również te, które mogą nie być już w liście projektów)
        const alertsWithClients = alerts.filter(alert => alert.project && alert.project.trim() !== '');
        const uniqueClientsFromAlerts = [...new Set(alertsWithClients.map(alert => alert.project))];
        
        // Połącz listy i usuń duplikaty
        const allClients = [...new Set([...projectsList, ...uniqueClientsFromAlerts])];
        
        setAvailableClients(allClients.sort());
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Błąd wczytywania klientów:', error);
        }
        setAvailableClients([]);
      }
    };

    // Wczytaj klientów przy starcie
    loadClients();

    // Nasłuchuj zmian w projektach
    const handleProjectsUpdate = () => {
      loadClients();
    };

    window.addEventListener('projects-updated', handleProjectsUpdate);
    return () => {
      window.removeEventListener('projects-updated', handleProjectsUpdate);
    };
  }, [alerts]); // Dodaj alerts do zależności, aby lista klientów aktualizowała się gdy zmienią się alerty

  // Filtruj i sortuj alerty
  const filteredAndSortedAlerts = useMemo(() => {
    return alerts
    .filter(alert => {
      if (filterStatus !== 'all' && alert.status !== filterStatus) return false;
      if (filterPriority !== 'all' && alert.priority !== filterPriority) return false;
      if (filterClient !== 'all' && alert.project !== filterClient) return false;
      return true;
    })
    .sort((a, b) => {
      // Najpierw przesuń zamknięte na koniec
      if (a.status === 'closed' && b.status !== 'closed') return 1;
      if (a.status !== 'closed' && b.status === 'closed') return -1;
      
      // Jeśli oba są zamknięte lub oba nie są zamknięte, sortuj normalnie
      let aValue, bValue;

      switch (sortBy) {
        case 'priority':
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'dueDate':
          aValue = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          bValue = b.dueDate ? new Date(b.dueDate).getTime() : 0;
          break;
        default: // createdAt
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [alerts, filterStatus, filterPriority, filterClient, sortBy, sortOrder]);

  // Otwórz alert po nawigacji z linku (np. z powiadomienia)
  useEffect(() => {
    if (!openAlertId) {
      openAlertIdHandledRef.current = false;
      return;
    }
    loadAlerts();
  }, [openAlertId]);

  useEffect(() => {
    if (!openAlertId || openAlertIdHandledRef.current || alerts.length === 0) return;
    const id = typeof openAlertId === 'number' ? openAlertId : parseInt(openAlertId, 10);
    const alert = alerts.find(a => a.id === id);
    if (!alert) return;
    openAlertIdHandledRef.current = true;
    setSelectedAlert(alert);
    const idx = filteredAndSortedAlerts.findIndex(a => a.id === alert.id);
    setSelectedAlertIndex(idx >= 0 ? idx : -1);
    if (onOpenAlertHandled) onOpenAlertHandled();
  }, [openAlertId, alerts, filteredAndSortedAlerts, onOpenAlertHandled]);

  // Obsługa klawiszy strzałek dla nawigacji
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Nawigacja między kartami w modalu alertu
      if (selectedAlert && selectedAlertIndex >= 0 && filteredAndSortedAlerts.length > 0) {
        if (e.key === 'ArrowLeft' && selectedAlertIndex > 0) {
          e.preventDefault();
          const prevIndex = selectedAlertIndex - 1;
          setSelectedAlert(filteredAndSortedAlerts[prevIndex]);
          setSelectedAlertIndex(prevIndex);
        } else if (e.key === 'ArrowRight' && selectedAlertIndex < filteredAndSortedAlerts.length - 1) {
          e.preventDefault();
          const nextIndex = selectedAlertIndex + 1;
          setSelectedAlert(filteredAndSortedAlerts[nextIndex]);
          setSelectedAlertIndex(nextIndex);
        } else if (e.key === 'Escape') {
          setSelectedAlert(null);
          setSelectedAlertIndex(-1);
        }
      }
      
      // Nawigacja między zdjęciami w modalu zdjęcia
      if (selectedImage && selectedImage.allImages && selectedImage.allImages.length > 0) {
        if (e.key === 'ArrowLeft' && selectedImage.imageIndex > 0) {
          e.preventDefault();
          const prevIndex = selectedImage.imageIndex - 1;
          const prevImage = selectedImage.allImages[prevIndex];
          setSelectedImage({
            ...selectedImage,
            src: prevImage.data,
            name: prevImage.name,
            imageIndex: prevIndex,
            comment: prevImage.comment,
            status: prevImage.status,
            arrows: prevImage.arrows || []
          });
        } else if (e.key === 'ArrowRight' && selectedImage.imageIndex < selectedImage.allImages.length - 1) {
          e.preventDefault();
          const nextIndex = selectedImage.imageIndex + 1;
          const nextImage = selectedImage.allImages[nextIndex];
          // Pobierz aktualne dane z alertów
          const currentAlert = alerts.find(a => a.id === selectedImage.alertId);
          const updatedAllImages = currentAlert?.images || selectedImage.allImages;
          setSelectedImage({
            ...selectedImage,
            src: nextImage.data,
            name: nextImage.name,
            imageIndex: nextIndex,
            allImages: updatedAllImages,
            comment: nextImage.comment,
            status: nextImage.status,
            arrows: updatedAllImages[nextIndex]?.arrows || nextImage.arrows || []
          });
        } else if (e.key === 'Escape') {
          setSelectedImage(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedAlert, selectedAlertIndex, selectedImage, filteredAndSortedAlerts]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Brak';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Brak';
    // SQLite zwraca czas w formacie "YYYY-MM-DD HH:MM:SS" (lokalny czas po użyciu datetime(..., 'localtime'))
    // JavaScript interpretuje format bez 'Z' jako lokalny czas, ale dla pewności upewnijmy się
    // Jeśli dateString nie ma 'T', dodajmy go, aby JavaScript poprawnie go zinterpretował
    let date;
    if (dateString.includes('T')) {
      // Format ISO 8601 (np. "2026-01-23T17:27:00")
      date = new Date(dateString);
    } else {
      // Format SQLite (np. "2026-01-23 17:27:00") - zamień spację na 'T' dla poprawnej interpretacji
      date = new Date(dateString.replace(' ', 'T'));
    }
    return date.toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Warsaw' // Upewnij się, że używamy polskiej strefy czasowej
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  // Funkcja do drukowania karty alertu
  const printAlert = (alert) => {
    // Utwórz nowe okno z zawartością do druku
    const printWindow = window.open('', '_blank');
    
    // Przygotuj HTML z kartą alertu
    const priorityColor = priorities[alert.priority]?.color || '#667eea';
    const priorityLabel = priorities[alert.priority]?.label || 'Średni';
    const statusColor = statuses[alert.status]?.color || '#10b981';
    const statusLabel = statuses[alert.status]?.label || 'Otwarty';
    
    // Filtruj tylko zdjęcia oznaczone do druku, potem sortuj: NOK pierwsze, potem OK, potem bez statusu
    const sortedImages = alert.images ? [...alert.images]
      .filter(img => img.printIncluded !== false) // Domyślnie true, więc tylko wykluczamy false
      .sort((a, b) => {
        if (a.status === 'NOK' && b.status !== 'NOK') return -1;
        if (a.status === 'OK' && b.status !== 'OK' && b.status !== 'NOK') return -1;
        if (a.status === 'NOK' && b.status === 'OK') return -1;
        return 0;
      }) : [];
    
    const imagesHtml = sortedImages.map((image, imgIndex) => {
      const arrowsHtml = (image.arrows || []).map((arrow) => {
        const arrowLength = 35;
        const angleRad = (arrow.angle * Math.PI) / 180;
        const centerX = arrow.x - (arrowLength * 0.5 * Math.sin(angleRad));
        const centerY = arrow.y + (arrowLength * 0.5 * Math.cos(angleRad));
        
        return `
          <div class="print-image-arrow" style="position: absolute; left: ${centerX}%; top: ${centerY}%; transform: translate(-50%, -50%) rotate(${arrow.angle}deg); pointer-events: none; z-index: 4;">
            <svg width="35" height="35" viewBox="0 0 40 40" style="transform: translateY(-17.5px);">
              <path d="M 20 0 L 0 30 L 15 30 L 15 40 L 25 40 L 25 30 L 40 30 Z" fill="#FFE066" stroke="#FFB84D" stroke-width="1.5" opacity="0.9" />
            </svg>
          </div>
        `;
      }).join('');
      
      const statusClass = image.status ? image.status.toLowerCase() : '';
      return `
        <div class="print-image-item" data-status="${image.status || ''}">
          <div class="print-image-wrapper">
            <img src="${image.data}" alt="${image.name || `Zdjęcie ${imgIndex + 1}`}" />
            <div class="print-image-status-overlay ${statusClass}"></div>
            ${arrowsHtml}
          </div>
          ${image.comment ? `<div class="print-image-comment">${image.comment}</div>` : ''}
        </div>
      `;
    }).join('');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Alert: ${alert.title}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            html, body {
              width: 100%;
              height: 100%;
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              background: white;
              overflow: auto;
            }
            body {
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
              background: white;
            }
            @media print {
              @page {
                size: A4;
                margin: 15mm;
              }
              html, body {
                width: 100%;
                height: auto;
                margin: 0;
                padding: 0;
                background: white;
                overflow: visible;
              }
              body {
                padding: 0;
                max-width: 100%;
                margin: 0;
              }
              /* Ukryj wszystkie elementy poza zawartością karty */
              body > *:not(.print-content) {
                display: none !important;
              }
              /* Ukryj nagłówki i stopki przeglądarki */
              @page {
                margin: 15mm;
              }
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 20px;
              position: relative;
            }
            .print-warning-sign {
              position: absolute;
              top: -20px;
              right: -20px;
              width: 160px;
              height: 160px;
              transform: rotate(45deg);
              z-index: 10;
              pointer-events: none;
            }
            .print-warning-sign svg {
              width: 100%;
              height: 100%;
            }
            .print-title {
              font-size: 24px;
              font-weight: bold;
              color: #333;
              flex: 1;
            }
            .print-badges {
              display: flex;
              gap: 8px;
            }
            .print-badge {
              padding: 4px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              color: white;
            }
            .print-details-grid {
              display: block;
              margin-bottom: 25px;
              padding: 20px;
              background: #f8f9fa;
              border-radius: 8px;
            }
            .print-details-row {
              display: flex;
              flex-direction: row;
              gap: 30px;
              align-items: center;
              margin-bottom: 15px;
              flex-wrap: nowrap;
            }
            .print-details-row:last-child {
              margin-bottom: 0;
            }
            .print-detail-item {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }
            .print-detail-item-inline {
              display: flex;
              flex-direction: row;
              gap: 8px;
              align-items: baseline;
            }
            .print-detail-item-inline:not(:last-child)::after {
              content: '|';
              margin-left: 15px;
              margin-right: 15px;
              color: #999;
              font-weight: normal;
              font-size: 14px;
            }
            .print-detail-item-inline .print-detail-label {
              margin: 0;
            }
            .print-detail-item-inline .print-detail-value {
              margin: 0;
            }
            .print-detail-label {
              font-size: 12px;
              color: #666;
              font-weight: 500;
              text-transform: uppercase;
            }
            .print-detail-value {
              font-size: 15px;
              color: #333;
              font-weight: 600;
            }
            .print-section {
              background: #f8f9fa;
              border: 2px solid #e9ecef;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 25px;
            }
            .print-section-title {
              font-size: 18px;
              font-weight: 600;
              margin: 0 0 12px 0;
              border-bottom: 2px solid #e0e0e0;
              padding-bottom: 10px;
            }
            .print-section-title.description {
              color: #dc2626;
            }
            .print-section-title.corrective {
              color: #10b981;
            }
            .print-section-content {
              color: #333;
              font-size: 15px;
              line-height: 1.7;
              margin: 0;
            }
            .print-images-grid {
              display: flex;
              flex-wrap: nowrap;
              justify-content: center;
              align-items: flex-start;
              gap: 20px;
            }
            .print-images-grid .print-image-item {
              position: relative;
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              overflow: hidden;
              background: #f8f9fa;
              flex: 0 0 calc(50% - 10px);
              max-width: calc(50% - 10px);
            }
            .print-images-grid-multiple {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 20px;
              justify-items: center;
            }
            .print-images-grid-multiple .print-image-item {
              position: relative;
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              overflow: hidden;
              background: #f8f9fa;
              width: 100%;
              max-width: 100%;
            }
            .print-image-wrapper {
              position: relative;
              width: 100%;
              aspect-ratio: 1;
              overflow: hidden;
            }
            .print-image-arrow {
              filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
              opacity: 0.85;
            }
            .print-image-arrow svg path {
              fill: #FFE066;
              stroke: #FFB84D;
              stroke-width: 1.5;
              opacity: 0.9;
            }
            .print-image-wrapper img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .print-image-status-overlay {
              position: absolute;
              top: 0;
              right: 0;
              width: 60px;
              height: 60px;
              clip-path: polygon(100% 0, 100% 100%, 0 0);
              pointer-events: none;
              z-index: 3;
              background: transparent;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
            .print-image-status-overlay.nok {
              background: #dc2626 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
            .print-image-status-overlay.nok::after {
              content: 'NOK';
              position: absolute;
              top: 15px;
              right: 3px;
              font-size: 12px;
              font-weight: 900;
              color: white;
              text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
              letter-spacing: 1px;
              transform: rotate(45deg);
              transform-origin: center;
              white-space: nowrap;
            }
            .print-image-status-overlay.ok {
              background: #10b981 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
            .print-image-status-overlay.ok::after {
              content: 'OK';
              position: absolute;
              top: 15px;
              right: 12px;
              font-size: 12px;
              font-weight: 900;
              color: white;
              text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
              letter-spacing: 1px;
              transform: rotate(45deg);
              transform-origin: center;
              white-space: nowrap;
            }
            .print-image-comment {
              padding: 12px;
              font-size: 14px;
              font-weight: 500;
              color: #333;
              text-align: center;
              background: #f8f9fa;
              border-top: 1px solid #e0e0e0;
              line-height: 1.5;
              word-wrap: break-word;
            }
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              body {
                padding: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                color-adjust: exact;
              }
              .print-section {
                page-break-inside: avoid;
              }
              .print-image-item {
                page-break-inside: avoid;
              }
              .print-image-status-overlay {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .print-image-status-overlay.nok {
                background: #dc2626 !important;
              }
              .print-image-status-overlay.ok {
                background: #10b981 !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-content">
          <div class="print-header">
            <h1 class="print-title">${alert.title || 'Alert Jakościowy'}</h1>
            <div class="print-warning-sign">
              <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" width="160" height="160">
                <defs>
                  <linearGradient id="triangleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#ef4444;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#dc2626;stop-opacity:1" />
                  </linearGradient>
                  <filter id="shadow">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                    <feOffset dx="2" dy="2" result="offsetblur"/>
                    <feComponentTransfer>
                      <feFuncA type="linear" slope="0.3"/>
                    </feComponentTransfer>
                    <feMerge>
                      <feMergeNode/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <path d="M60 15 L105 95 L15 95 Z" 
                      fill="url(#triangleGradient)" 
                      stroke="#b91c1c" 
                      stroke-width="2.5"
                      stroke-linejoin="round"
                      filter="url(#shadow)"/>
                <path d="M60 15 L105 95 L15 95 Z" 
                      fill="none" 
                      stroke="#ffffff" 
                      stroke-width="1"
                      stroke-opacity="0.3"
                      stroke-linejoin="round"/>
                <text x="60" y="75" 
                      font-size="70" 
                      font-weight="900" 
                      fill="white" 
                      text-anchor="middle" 
                      dominant-baseline="middle"
                      font-family="Arial, sans-serif"
                      stroke="#b91c1c"
                      stroke-width="0.5"
                      opacity="0.95">!</text>
              </svg>
            </div>
          </div>
          
          <div class="print-details-grid">
            <div class="print-details-row">
              ${alert.category ? `
                <div class="print-detail-item-inline">
                  <span class="print-detail-label">Kategoria:</span>
                  <span class="print-detail-value">${alert.category}</span>
                </div>
              ` : ''}
              ${alert.project ? `
                <div class="print-detail-item-inline">
                  <span class="print-detail-label">Klient:</span>
                  <span class="print-detail-value">${alert.project}</span>
                </div>
              ` : ''}
              <div class="print-detail-item-inline">
                <span class="print-detail-label">Utworzony:</span>
                <span class="print-detail-value">${formatDateTime(alert.createdAt)}</span>
              </div>
            </div>
            ${alert.dueDate ? `
              <div class="print-detail-item">
                <span class="print-detail-label">Termin:</span>
                <span class="print-detail-value" style="color: ${isOverdue(alert.dueDate) ? '#dc2626' : '#333'}">${formatDate(alert.dueDate)}</span>
              </div>
            ` : ''}
          </div>
          
          <div class="print-section">
            <h3 class="print-section-title description">Opis</h3>
            <p class="print-section-content">${alert.description || 'Brak opisu'}</p>
          </div>
          
          ${sortedImages.length > 0 ? `
            <div class="print-section">
              <div class="${sortedImages.length <= 2 ? 'print-images-grid' : 'print-images-grid-multiple'}">
                ${imagesHtml}
              </div>
            </div>
          ` : ''}
          
          ${alert.correctiveActions ? `
            <div class="print-section">
              <h3 class="print-section-title corrective">Kroki zaradcze</h3>
              <p class="print-section-content">${alert.correctiveActions}</p>
            </div>
          ` : ''}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Poczekaj na załadowanie obrazów, potem wydrukuj
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className="quality-container">
      <div className="quality-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="quality-title" style={{ margin: 0 }}>
            {showAddForm ? 'Dodaj Alert Jakościowy' : 'Tablica Alertów Jakościowych'}
          </h2>
          {hasQualityPermission(user, 'create') && (
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="btn-add-alert"
              style={{
                padding: '10px 20px',
                backgroundColor: showAddForm ? '#6b7280' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
            >
              {showAddForm ? 'Anuluj' : 'Dodaj'}
            </button>
          )}
        </div>
        
        {/* Formularz dodawania alertu */}
        {showAddForm && (
          <AddAlertForm 
            user={user} 
            projects={projects}
            users={allUsers}
            onSuccess={() => {
              setShowAddForm(false);
              loadAlerts();
              // Wyślij event, aby Dashboard mógł odświeżyć powiadomienia
              window.dispatchEvent(new CustomEvent('quality-alert-created'));
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}
        
        {/* Lista alertów - pokazuj tylko gdy formularz jest ukryty */}
        {!showAddForm && (
          <div>
            <p className="quality-description">
              Przegląd wszystkich alertów jakościowych
            </p>

            {/* Filtry i sortowanie */}
            <div className="quality-filters">
              <div className="filter-group">
            <label htmlFor="filter-status">Status:</label>
            <select
              id="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">Wszystkie</option>
              <option value="open">Otwarty</option>
              <option value="resolved">Rozwiązany</option>
              <option value="closed">Zamknięty</option>
            </select>
              </div>

              <div className="filter-group">
            <label htmlFor="filter-priority">Priorytet:</label>
            <select
              id="filter-priority"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="filter-select"
            >
              <option value="all">Wszystkie</option>
              <option value="critical">Krytyczny</option>
              <option value="high">Wysoki</option>
              <option value="medium">Średni</option>
              <option value="low">Niski</option>
            </select>
              </div>

              <div className="filter-group">
            <label htmlFor="filter-client">Klient:</label>
            <select
              id="filter-client"
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="filter-select"
            >
              <option value="all">Wszyscy</option>
              {availableClients.map((client, idx) => (
                <option key={idx} value={client}>
                  {client}
                </option>
              ))}
            </select>
              </div>

              <div className="filter-group">
            <label htmlFor="sort-by">Sortuj według:</label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="createdAt">Data utworzenia</option>
              <option value="priority">Priorytet</option>
              <option value="dueDate">Termin</option>
            </select>
              </div>

              <div className="filter-group">
            <label htmlFor="sort-order">Kolejność:</label>
            <select
              id="sort-order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="filter-select"
            >
              <option value="desc">Malejąco</option>
              <option value="asc">Rosnąco</option>
            </select>
              </div>
            </div>

            {/* Statystyki */}
            <div className="quality-stats">
          <div className="stat-card">
              <div className="stat-value">{alerts.length}</div>
              <div className="stat-label">Wszystkie alerty</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#3b82f6' }}>
                {alerts.filter(a => a.status === 'open').length}
              </div>
              <div className="stat-label">Otwarte</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#10b981' }}>
                {alerts.filter(a => a.status === 'resolved').length}
              </div>
              <div className="stat-label">Rozwiązane</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#ef4444' }}>
                {alerts.filter(a => a.priority === 'critical' || a.priority === 'high').length}
              </div>
              <div className="stat-label">Wysoki priorytet</div>
            </div>
          </div>

          {/* Lista alertów */}
          {filteredAndSortedAlerts.length === 0 ? (
          <div className="no-alerts">
            <p>Brak alertów do wyświetlenia</p>
            <p className="no-alerts-hint">Kliknij przycisk "Dodaj" powyżej, aby dodać pierwszy alert</p>
          </div>
        ) : (
          <div className="alerts-grid">
            {filteredAndSortedAlerts.map((alert, index) => (
              <div 
                key={alert.id} 
                className="alert-card"
                onClick={() => {
                  setSelectedAlert(alert);
                  setSelectedAlertIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="alert-header">
                  <h3 className="alert-title">{alert.title}</h3>
                  <div className="alert-badges">
                    <span
                      className="badge priority-badge"
                      style={{ backgroundColor: priorities[alert.priority]?.color }}
                    >
                      {priorities[alert.priority]?.label}
                    </span>
                    <div className="status-selector-wrapper">
                      <select
                        value={alert.status}
                        onChange={(e) => updateAlertStatus(alert.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="status-select"
                        disabled={!hasQualityPermission(user, 'changeStatus')}
                        style={{ 
                          backgroundColor: statuses[alert.status]?.color,
                          color: 'white',
                          border: 'none',
                          cursor: hasQualityPermission(user, 'changeStatus') ? 'pointer' : 'default',
                          opacity: hasQualityPermission(user, 'changeStatus') ? 1 : 0.7
                        }}
                      >
                        {Object.entries(statuses).map(([value, status]) => (
                          <option key={value} value={value} style={{ backgroundColor: 'white', color: '#333' }}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {hasQualityPermission(user, 'delete') && (
                      <button
                        className="alert-delete-btn"
                        onClick={(e) => handleDeleteClick(alert, e)}
                        title="Usuń alert"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#dc2626',
                          fontSize: '18px',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#fee2e2'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {/* Opis - zawsze wyświetlaj, nawet jeśli pusty */}
                <div 
                  className="alert-description"
                  dangerouslySetInnerHTML={{ __html: alert.description && alert.description.trim() ? alert.description : 'Brak opisu' }}
                />

                {/* Kroki zaradcze - wyświetlaj jeśli istnieje i nie jest pusty */}
                {alert.correctiveActions && alert.correctiveActions.trim() && (
                  <div className="alert-corrective-actions">
                    <span className="detail-label" style={{ fontWeight: 600, display: 'block', marginBottom: '5px' }}>
                      Kroki zaradcze:
                    </span>
                    <div 
                      className="alert-description" 
                      style={{ marginTop: '5px', fontSize: '13px' }}
                      dangerouslySetInnerHTML={{ __html: alert.correctiveActions }}
                    />
                  </div>
                )}

                <div className="alert-details">
                  {alert.category && (
                    <div className="detail-item">
                      <span className="detail-label">Kategoria:</span>
                      <span className="detail-value">{alert.category}</span>
                    </div>
                  )}
                  {alert.project && (
                    <div className="detail-item">
                      <span className="detail-label">Klient:</span>
                      <span className="detail-value">{alert.project}</span>
                    </div>
                  )}
                  {alert.assignedTo && (
                    <div className="detail-item">
                      <span className="detail-label">Przypisany do:</span>
                      <span className="detail-value">{alert.assignedTo}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Utworzony:</span>
                    <span className="detail-value">{formatDateTime(alert.createdAt)}</span>
                  </div>
                  {alert.dueDate && (
                    <div className="detail-item">
                      <span className="detail-label">Termin:</span>
                      <span className={`detail-value ${isOverdue(alert.dueDate) ? 'overdue' : ''}`}>
                        {formatDate(alert.dueDate)}
                        {isOverdue(alert.dueDate) && ' ⚠️'}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Utworzony przez:</span>
                    <span className="detail-value">{alert.createdBy}</span>
                  </div>
                </div>

                {/* Zdjęcia */}
                {alert.images && alert.images.length > 0 && (
                  <div className="alert-images" onClick={(e) => e.stopPropagation()}>
                    <div className="alert-images-grid">
                      {alert.images
                        .sort((a, b) => {
                          // Sortowanie: NOK pierwsze, potem OK, potem bez statusu
                          if (a.status === 'NOK' && b.status !== 'NOK') return -1;
                          if (a.status === 'OK' && b.status !== 'OK' && b.status !== 'NOK') return -1;
                          if (a.status === 'NOK' && b.status === 'OK') return -1;
                          return 0;
                        })
                        .map((image, imgIndex) => (
                        <div
                          key={imgIndex}
                          className="alert-image-thumbnail"
                          data-status={image.status || ''}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage({ 
                              src: image.data, 
                              name: image.name,
                              alertId: alert.id,
                              imageIndex: imgIndex,
                              allImages: alert.images || [],
                              comment: image.comment,
                              status: image.status,
                              arrows: image.arrows || []
                            });
                          }}
                          title={image.comment || image.name || `Zdjęcie ${imgIndex + 1}`}
                        >
                          <img src={image.data} alt={image.name || `Zdjęcie ${imgIndex + 1}`} />
                          {image.status && (
                            <div className={`image-status-overlay ${image.status.toLowerCase()}`}></div>
                          )}
                          {(image.arrows || []).map((arrow) => {
                            const arrowLength = 30;
                            const angleRad = (arrow.angle * Math.PI) / 180;
                            const centerX = arrow.x - (arrowLength * 0.5 * Math.sin(angleRad));
                            const centerY = arrow.y + (arrowLength * 0.5 * Math.cos(angleRad));
                            
                            return (
                              <div
                                key={arrow.id}
                                className="image-arrow"
                                style={{
                                  position: 'absolute',
                                  left: `${centerX}%`,
                                  top: `${centerY}%`,
                                  transform: `translate(-50%, -50%) rotate(${arrow.angle}deg)`,
                                  pointerEvents: 'none',
                                  zIndex: 4
                                }}
                              >
                                <svg width="30" height="30" viewBox="0 0 40 40" style={{ transform: 'translateY(-15px)' }}>
                                  <path
                                    d="M 20 0 L 0 30 L 15 30 L 15 40 L 25 40 L 25 30 L 40 30 Z"
                                    fill="#FFE066"
                                    stroke="#FFB84D"
                                    strokeWidth="1.5"
                                    opacity="0.9"
                                  />
                                </svg>
                              </div>
                            );
                          })}
                          {image.comment && (
                            <div className="image-comment" title={image.comment}>
                              {image.comment}
                            </div>
                          )}
                          <div className="image-overlay">
                            <span className="zoom-icon">🔍</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
          </div>
        )}

        {/* Modal do wyświetlania szczegółów alertu */}
        {selectedAlert && selectedAlertIndex >= 0 && (
          <div className="alert-modal-overlay" onClick={() => {
            setSelectedAlert(null);
            setSelectedAlertIndex(-1);
          }}>
            <div className="alert-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="alert-modal-close" onClick={() => {
                setSelectedAlert(null);
                setSelectedAlertIndex(-1);
              }}>×</button>
              
              {/* Nawigacja między kartami */}
              {filteredAndSortedAlerts.length > 1 && (
                <>
                  {selectedAlertIndex > 0 && (
                    <button 
                      className="alert-modal-nav alert-modal-nav-prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        const prevIndex = selectedAlertIndex - 1;
                        setSelectedAlert(filteredAndSortedAlerts[prevIndex]);
                        setSelectedAlertIndex(prevIndex);
                      }}
                      title="Poprzedni alert (←)"
                    >
                      ‹
                    </button>
                  )}
                  {selectedAlertIndex < filteredAndSortedAlerts.length - 1 && (
                    <button 
                      className="alert-modal-nav alert-modal-nav-next"
                      onClick={(e) => {
                        e.stopPropagation();
                        const nextIndex = selectedAlertIndex + 1;
                        setSelectedAlert(filteredAndSortedAlerts[nextIndex]);
                        setSelectedAlertIndex(nextIndex);
                      }}
                      title="Następny alert (→)"
                    >
                      ›
                    </button>
                  )}
                </>
              )}
              
              <div className="alert-modal-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <h2 className="alert-modal-title">{selectedAlert.title}</h2>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {hasQualityPermission(user, 'delete') && (
                      <button
                        className="btn-delete-alert"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(selectedAlert, e);
                        }}
                        title="Usuń alert"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                      >
                        🗑️ Usuń
                      </button>
                    )}
                    {hasQualityPermission(user, 'editAlerts') && (
                      <button
                        className="btn-edit-alert"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditAlert(selectedAlert);
                          setSelectedAlert(null);
                          setSelectedAlertIndex(-1);
                        }}
                        title="Edytuj alert"
                        style={{ padding: '10px 20px', fontSize: '14px' }}
                      >
                        ✏️ Edytuj
                      </button>
                    )}
                    {hasQualityPermission(user, 'print') && (
                      <button
                        className="btn-print"
                        onClick={(e) => {
                          e.stopPropagation();
                          printAlert(selectedAlert);
                        }}
                        title="Drukuj kartę"
                      >
                        🖨️ Drukuj
                      </button>
                    )}
                  </div>
                </div>
                <div className="alert-modal-badges">
                  <span
                    className="badge priority-badge"
                    style={{ backgroundColor: priorities[selectedAlert.priority]?.color }}
                  >
                    {priorities[selectedAlert.priority]?.label}
                  </span>
                  <div className="status-selector-wrapper">
                    <select
                      value={selectedAlert.status}
                      onChange={(e) => {
                        updateAlertStatus(selectedAlert.id, e.target.value);
                        setSelectedAlert({ ...selectedAlert, status: e.target.value });
                      }}
                      className="status-select"
                      disabled={!hasQualityPermission(user, 'changeStatus')}
                      style={{ 
                        backgroundColor: statuses[selectedAlert.status]?.color,
                        color: 'white',
                        border: 'none',
                        cursor: hasQualityPermission(user, 'changeStatus') ? 'pointer' : 'default',
                        opacity: hasQualityPermission(user, 'changeStatus') ? 1 : 0.7
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {Object.entries(statuses).map(([value, status]) => (
                        <option key={value} value={value} style={{ backgroundColor: 'white', color: '#333' }}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="alert-modal-details-grid">
                {selectedAlert.category && (
                  <div className="alert-modal-detail-item">
                    <span className="detail-label">Kategoria:</span>
                    <span className="detail-value">{selectedAlert.category}</span>
                  </div>
                )}
                {selectedAlert.project && (
                  <div className="alert-modal-detail-item">
                    <span className="detail-label">Klient:</span>
                    <span className="detail-value">{selectedAlert.project}</span>
                  </div>
                )}
                {selectedAlert.assignedTo && (
                  <div className="alert-modal-detail-item">
                    <span className="detail-label">Przypisany do:</span>
                    <span className="detail-value">{selectedAlert.assignedTo}</span>
                  </div>
                )}
                <div className="alert-modal-detail-item">
                  <span className="detail-label">Utworzony:</span>
                  <span className="detail-value">{formatDateTime(selectedAlert.createdAt)}</span>
                </div>
                {selectedAlert.updatedAt && selectedAlert.updatedAt !== selectedAlert.createdAt && (
                  <div className="alert-modal-detail-item">
                    <span className="detail-label">Ostatnia aktualizacja:</span>
                    <span className="detail-value">{formatDateTime(selectedAlert.updatedAt)}</span>
                  </div>
                )}
                {selectedAlert.dueDate && (
                  <div className="alert-modal-detail-item">
                    <span className="detail-label">Termin:</span>
                    <span className={`detail-value ${isOverdue(selectedAlert.dueDate) ? 'overdue' : ''}`}>
                      {formatDate(selectedAlert.dueDate)}
                      {isOverdue(selectedAlert.dueDate) && ' ⚠️'}
                    </span>
                  </div>
                )}
                <div className="alert-modal-detail-item">
                  <span className="detail-label">Utworzony przez:</span>
                  <span className="detail-value">{selectedAlert.createdBy}</span>
                </div>
              </div>

              {/* Opis błędu - pod modułem informacji */}
              {/* Opis - zawsze wyświetlaj sekcję */}
              <div className="alert-modal-section description-section">
                <h3 className="alert-modal-section-title">Opis</h3>
                <div 
                  className="alert-modal-description"
                  dangerouslySetInnerHTML={{ __html: selectedAlert.description && selectedAlert.description.trim() ? selectedAlert.description : 'Brak opisu' }}
                />
              </div>

              {/* Zdjęcia w modalu */}
              {selectedAlert.images && selectedAlert.images.length > 0 && (
                <div className="alert-modal-section images-section">
                  <div className="alert-modal-images-grid">
                    {selectedAlert.images
                      .sort((a, b) => {
                        // Sortowanie: NOK pierwsze, potem OK, potem bez statusu
                        if (a.status === 'NOK' && b.status !== 'NOK') return -1;
                        if (a.status === 'OK' && b.status !== 'OK' && b.status !== 'NOK') return -1;
                        if (a.status === 'NOK' && b.status === 'OK') return -1;
                        return 0;
                      })
                      .map((image, imgIndex) => (
                      <div
                        key={imgIndex}
                        className="alert-modal-image-item"
                        data-status={image.status || ''}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage({ 
                            src: image.data, 
                            name: image.name,
                            alertId: selectedAlert.id,
                            imageIndex: imgIndex,
                            allImages: selectedAlert.images || [],
                            comment: image.comment,
                            status: image.status,
                            arrow: image.arrow
                          });
                        }}
                      >
                        <img src={image.data} alt={image.name || `Zdjęcie ${imgIndex + 1}`} />
                        {image.status && (
                          <div className={`image-status-overlay ${image.status.toLowerCase()}`}></div>
                        )}
                        {image.arrow && (
                          <div 
                            className="image-arrow"
                            style={{
                              position: 'absolute',
                              left: `${image.arrow.x}%`,
                              top: `${image.arrow.y}%`,
                              transform: 'translate(-50%, -100%)',
                              pointerEvents: 'none',
                              zIndex: 4
                            }}
                          >
                            <svg width="35" height="35" viewBox="0 0 40 40">
                              <path
                                d="M 20 0 L 0 30 L 15 30 L 15 40 L 25 40 L 25 30 L 40 30 Z"
                                fill="#FFE066"
                                stroke="#FFB84D"
                                strokeWidth="1.5"
                                opacity="0.9"
                              />
                            </svg>
                          </div>
                        )}
                        {(image.comment || image.name) && (
                          <div className="alert-modal-image-name" title={image.comment || image.name}>
                            {image.comment || image.name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kroki zaradcze - pod zdjęciami - wyświetlaj jeśli istnieje i nie jest pusty */}
              {selectedAlert.correctiveActions && selectedAlert.correctiveActions.trim() && (
                <div className="alert-modal-section corrective-actions-section">
                  <h3 className="alert-modal-section-title">Kroki zaradcze</h3>
                  <div 
                    className="alert-modal-description"
                    dangerouslySetInnerHTML={{ __html: selectedAlert.correctiveActions }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal do wyświetlania zdjęcia w pełnym rozmiarze */}
        {selectedImage && selectedImage.allImages && selectedImage.allImages.length > 0 && (
          <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="image-modal-close" onClick={() => setSelectedImage(null)}>×</button>
              
              {/* Nawigacja między zdjęciami */}
              {selectedImage.allImages.length > 1 && (
                <>
                  {selectedImage.imageIndex > 0 && (
                    <button 
                      className="image-modal-nav image-modal-nav-prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        const prevIndex = selectedImage.imageIndex - 1;
                        const prevImage = selectedImage.allImages[prevIndex];
                        setSelectedImage({
                          src: prevImage.data,
                          name: prevImage.name,
                          alertId: selectedImage.alertId,
                          imageIndex: prevIndex,
                          allImages: selectedImage.allImages,
                          comment: prevImage.comment,
                          status: prevImage.status,
                          arrows: prevImage.arrows || []
                        });
                      }}
                      title="Poprzednie zdjęcie (←)"
                    >
                      ‹
                    </button>
                  )}
                  {selectedImage.imageIndex < selectedImage.allImages.length - 1 && (
                    <button 
                      className="image-modal-nav image-modal-nav-next"
                      onClick={(e) => {
                        e.stopPropagation();
                        const nextIndex = selectedImage.imageIndex + 1;
                        const nextImage = selectedImage.allImages[nextIndex];
                        // Pobierz aktualne dane z alertów
                        const currentAlert = alerts.find(a => a.id === selectedImage.alertId);
                        const updatedAllImages = currentAlert?.images || selectedImage.allImages;
                        setSelectedImage({
                          src: nextImage.data,
                          name: nextImage.name,
                          alertId: selectedImage.alertId,
                          imageIndex: nextIndex,
                          allImages: updatedAllImages,
                          comment: nextImage.comment,
                          status: nextImage.status,
                          arrows: updatedAllImages[nextIndex]?.arrows || nextImage.arrows || []
                        });
                      }}
                      title="Następne zdjęcie (→)"
                    >
                      ›
                    </button>
                  )}
                </>
              )}
              
              <div className="image-modal-counter">
                {selectedImage.imageIndex + 1} / {selectedImage.allImages.length}
              </div>
              
              <div 
                style={{ position: 'relative', display: 'inline-block', cursor: 'crosshair' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const imgElement = e.currentTarget.querySelector('img');
                  if (!imgElement) return;
                  
                  // Sprawdź czy kliknięto w kontrolkę strzałki (przycisk obrotu lub usuwania)
                  if (e.target.closest('.arrow-control')) {
                    return;
                  }
                  
                  const rect = imgElement.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  
                  // Sprawdź czy kliknięto w istniejącą strzałkę
                  const currentArrows = selectedImage.arrows || [];
                  let clickedArrow = null;
                  for (const arrow of currentArrows) {
                    const distance = Math.sqrt(Math.pow(x - arrow.x, 2) + Math.pow(y - arrow.y, 2));
                    if (distance < 3) {
                      clickedArrow = arrow;
                      break;
                    }
                  }
                  
                  if (clickedArrow) {
                    // Usuń strzałkę przy podwójnym kliknięciu
                    if (e.detail === 2) {
                      removeImageArrow(selectedImage.alertId, selectedImage.imageIndex, clickedArrow.id);
                    }
                    return;
                  }
                  
                  // Dodaj nową strzałkę (grot w miejscu kliknięcia)
                  addImageArrow(selectedImage.alertId, selectedImage.imageIndex, x, y);
                }}
              >
                <img src={selectedImage.src} alt={selectedImage.name} className="image-modal-image" />
                {selectedImage.status && (
                  <div className={`image-status-overlay ${selectedImage.status.toLowerCase()}`}></div>
                )}
                {(selectedImage.arrows || []).map((arrow) => {
                  // Oblicz pozycję środka strzałki (grot jest w miejscu kliknięcia)
                  const arrowLength = 40; // Długość strzałki w px
                  const angleRad = (arrow.angle * Math.PI) / 180;
                  const centerX = arrow.x - (arrowLength * 0.5 * Math.sin(angleRad));
                  const centerY = arrow.y + (arrowLength * 0.5 * Math.cos(angleRad));
                  
                  return (
                    <div
                      key={arrow.id}
                      className="image-arrow arrow-control"
                      style={{
                        position: 'absolute',
                        left: `${centerX}%`,
                        top: `${centerY}%`,
                        transform: `translate(-50%, -50%) rotate(${arrow.angle}deg)`,
                        pointerEvents: 'auto',
                        zIndex: 1004,
                        cursor: 'move'
                      }}
                    >
                      <svg width="50" height="50" viewBox="0 0 40 40" style={{ transform: 'translateY(-20px)' }}>
                        <path
                          d="M 20 0 L 0 30 L 15 30 L 15 40 L 25 40 L 25 30 L 40 30 Z"
                          fill="#FFE066"
                          stroke="#FFB84D"
                          strokeWidth="1.5"
                          opacity="0.9"
                        />
                      </svg>
                      <div
                        className="arrow-control"
                        style={{
                          position: 'absolute',
                          top: '-30px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          gap: '5px',
                          background: 'rgba(255, 255, 255, 0.9)',
                          padding: '5px',
                          borderRadius: '5px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                        }}
                      >
                        <button
                          type="button"
                          className="arrow-control"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateArrowAngle(selectedImage.alertId, selectedImage.imageIndex, arrow.id, arrow.angle - 15);
                          }}
                          title="Obróć w lewo"
                          style={{
                            background: '#f0f0f0',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            padding: '3px 8px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ↺
                        </button>
                        <button
                          type="button"
                          className="arrow-control"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImageArrow(selectedImage.alertId, selectedImage.imageIndex, arrow.id);
                          }}
                          title="Usuń strzałkę"
                          style={{
                            background: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '3px 8px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ×
                        </button>
                        <button
                          type="button"
                          className="arrow-control"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateArrowAngle(selectedImage.alertId, selectedImage.imageIndex, arrow.id, arrow.angle + 15);
                          }}
                          title="Obróć w prawo"
                          style={{
                            background: '#f0f0f0',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            padding: '3px 8px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ↻
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: '15px', textAlign: 'center', color: '#666', fontStyle: 'italic', fontSize: '14px' }}>
                Kliknij na zdjęcie, aby dodać strzałkę • Podwójne kliknięcie na strzałkę, aby ją usunąć • Użyj przycisków, aby obrócić strzałkę
              </div>
              {selectedImage.name && (
                <div className="image-modal-name">{selectedImage.name}</div>
              )}
              {selectedImage.comment && (
                <div className="image-modal-name" style={{ marginTop: '10px', fontStyle: 'italic', color: '#666' }}>
                  Komentarz: {selectedImage.comment}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal edycji alertu */}
        {editingAlert && editFormData && (
          <div className="alert-modal-overlay" onClick={closeEditAlert}>
            <div className="alert-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
              <button className="alert-modal-close" onClick={closeEditAlert}>×</button>
              
              <div className="alert-modal-header">
                <h2 className="alert-modal-title">Edytuj Alert</h2>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); updateAlert(); }} className="quality-form">
                <div className="form-group">
                  <label htmlFor="edit-title" className="form-label">
                    Tytuł alertu: <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="edit-title"
                    name="title"
                    value={editFormData.title}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="form-input"
                    placeholder="np. Błąd w procesie produkcyjnym"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-description" className="form-label">
                    Opis:
                  </label>
                  <textarea
                    id="edit-description"
                    name="description"
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    className="form-textarea"
                    rows="5"
                    placeholder="Szczegółowy opis problemu..."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-priority" className="form-label">
                      Priorytet:
                    </label>
                    <select
                      id="edit-priority"
                      name="priority"
                      value={editFormData.priority}
                      onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                      className="form-select"
                    >
                      {Object.entries(priorities).map(([value, priority]) => (
                        <option key={value} value={value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-status" className="form-label">
                      Status:
                    </label>
                    <select
                      id="edit-status"
                      name="status"
                      value={editFormData.status}
                      onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                      className="form-select"
                    >
                      {Object.entries(statuses).map(([value, status]) => (
                        <option key={value} value={value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-category" className="form-label">
                      Kategoria:
                    </label>
                    <select
                      id="edit-category"
                      name="category"
                      value={editFormData.category}
                      onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                      className="form-select"
                    >
                      <option value="">-- Wybierz kategorię --</option>
                      {(() => {
                        try {
                          const stored = localStorage.getItem('quality_categories');
                          const cats = stored ? JSON.parse(stored) : [];
                          return cats.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ));
                        } catch {
                          return [];
                        }
                      })()}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-project" className="form-label">
                      Klient:
                    </label>
                    <input
                      type="text"
                      id="edit-project"
                      name="project"
                      value={editFormData.project}
                      onChange={(e) => setEditFormData({ ...editFormData, project: e.target.value })}
                      className="form-input"
                      placeholder="Nazwa klienta"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-assignedTo" className="form-label">
                      Przypisany do:
                    </label>
                    <input
                      type="text"
                      id="edit-assignedTo"
                      name="assignedTo"
                      value={editFormData.assignedTo}
                      onChange={(e) => setEditFormData({ ...editFormData, assignedTo: e.target.value })}
                      className="form-input"
                      placeholder="Email lub nazwa użytkownika"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-errorDate" className="form-label">
                      Data powstania błędu:
                    </label>
                    <input
                      type="date"
                      id="edit-errorDate"
                      name="errorDate"
                      value={editFormData.errorDate}
                      onChange={(e) => setEditFormData({ ...editFormData, errorDate: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-dueDate" className="form-label">
                      Termin rozwiązania:
                    </label>
                    <input
                      type="date"
                      id="edit-dueDate"
                      name="dueDate"
                      value={editFormData.dueDate}
                      onChange={(e) => setEditFormData({ ...editFormData, dueDate: e.target.value })}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-correctiveActions" className="form-label">
                      Kroki zaradcze:
                    </label>
                    <textarea
                      id="edit-correctiveActions"
                      name="correctiveActions"
                      value={editFormData.correctiveActions}
                      onChange={(e) => setEditFormData({ ...editFormData, correctiveActions: e.target.value })}
                      className="form-textarea"
                      rows="4"
                      placeholder="Opisz kroki, które należy podjąć, aby naprawić błąd..."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Zdjęcia (max 5, każdy max 5MB):
                  </label>
                  <div className="images-section">
                    {editImages.length < 5 && (
                      <button
                        type="button"
                        className="btn-add-image"
                        onClick={handleAddImageEdit}
                        title="Dodaj zdjęcie"
                      >
                        +
                      </button>
                    )}
                    {editImages.length > 0 && (
                      <div className="images-preview">
                        <div className="images-grid">
                          {editImages.map((image, index) => (
                            <div key={index} className="image-preview-item">
                              <div 
                                className="image-wrapper"
                                onClick={(e) => handleImageClickEdit(index, e)}
                                style={{ cursor: 'crosshair', position: 'relative' }}
                              >
                                <img src={image.data} alt={image.name} className="preview-image" />
                                {image.status && (
                                  <div className={`image-status-overlay ${image.status.toLowerCase()}`}></div>
                                )}
                                {image.arrow && (
                                  <div 
                                    className="image-arrow"
                                    style={{
                                      position: 'absolute',
                                      left: `${image.arrow.x}%`,
                                      top: `${image.arrow.y}%`,
                                      transform: 'translate(-50%, -100%)',
                                      pointerEvents: 'none',
                                      zIndex: 4
                                    }}
                                  >
                                    <svg width="30" height="30" viewBox="0 0 40 40">
                                      <path
                                        d="M 20 0 L 0 30 L 15 30 L 15 40 L 25 40 L 25 30 L 40 30 Z"
                                        fill="#FFE066"
                                        stroke="#FFB84D"
                                        strokeWidth="1.5"
                                        opacity="0.9"
                                      />
                                    </svg>
                                  </div>
                                )}
                                {image.arrow && (
                                  <button
                                    type="button"
                                    className="remove-arrow-btn"
                                    onClick={(e) => removeImageArrowEdit(index, e)}
                                    title="Usuń strzałkę"
                                  >
                                    ×
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="remove-image-btn"
                                  onClick={() => removeImageEdit(index)}
                                  title="Usuń zdjęcie"
                                >
                                  ×
                                </button>
                              </div>
                              <div className="image-arrow-hint">
                                {image.arrow ? 'Kliknij na strzałkę, aby ją usunąć' : 'Kliknij na zdjęcie, aby dodać strzałkę wskazującą wadę'}
                              </div>
                              <input
                                type="text"
                                value={image.comment || ''}
                                onChange={(e) => updateImageCommentEdit(index, e.target.value)}
                                className="image-comment-input"
                                placeholder="Dodaj komentarz..."
                              />
                              <div className="image-status-buttons">
                                <button
                                  type="button"
                                  className={`btn-status ${image.status === 'NOK' ? 'active nok' : 'nok'}`}
                                  onClick={() => updateImageStatusEdit(index, image.status === 'NOK' ? null : 'NOK')}
                                >
                                  NOK
                                </button>
                                <button
                                  type="button"
                                  className={`btn-status ${image.status === 'OK' ? 'active ok' : 'ok'}`}
                                  onClick={() => updateImageStatusEdit(index, image.status === 'OK' ? null : 'OK')}
                                >
                                  OK
                                </button>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                  <input
                                    type="checkbox"
                                    checked={image.printIncluded !== false}
                                    onChange={(e) => updateImagePrintIncluded(index, e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ cursor: 'pointer', width: '16px', height: '16px', margin: 0 }}
                                  />
                                  <span>Drukuj na karcie</span>
                                </label>
                              </div>
                              <span className="image-name">{image.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-submit">
                    Zapisz zmiany
                  </button>
                  <button
                    type="button"
                    className="btn-reset"
                    onClick={closeEditAlert}
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal potwierdzenia usunięcia */}
        {showDeleteModal && alertToDelete && (
          <div className="alert-modal-overlay" onClick={handleDeleteCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h3>Potwierdź usunięcie</h3>
                <button className="modal-close" onClick={handleDeleteCancel}>×</button>
              </div>
              <div style={{ padding: '24px' }}>
                <p style={{ marginBottom: '16px', fontSize: '16px' }}>
                  Czy na pewno chcesz usunąć alert <strong>"{alertToDelete.title}"</strong>?
                </p>
                <p style={{ marginBottom: '24px', color: '#dc2626', fontSize: '14px' }}>
                  ⚠️ Ta operacja jest nieodwracalna. Wszystkie dane alertu, w tym zdjęcia, zostaną trwale usunięte.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleDeleteCancel}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Usuń
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Komponent formularza dodawania alertu (przeniesiony z Dodaj.js)
// Domyślnie data = dziś, godzina = pusta – puste = powiadomienie od razu; wpisana godzina = zaplanowane (bez „przeskoku” na aktualną)
function getDefaultNotificationDateTime() {
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  return { date, time: '' };
}

// Edytor tekstu (opis / kroki zaradcze) – na poziomie modułu, żeby nie był odmontowywany
// przy każdej zmianie innych pól formularza (w przeciwnym razie treść się zerowała).
function AddAlertTextEditor({ placeholder, textareaRef, rows = 5, resetKey = 0 }) {
  const lastResetKeyRef = useRef(-1);
  const isInitialMountRef = useRef(true);
  const applyFormat = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
  }, []);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (textareaRef.current && resetKey !== lastResetKeyRef.current && resetKey > 0) {
      textareaRef.current.innerHTML = '';
      lastResetKeyRef.current = resetKey;
    }
  }, [resetKey, textareaRef]);

  return (
    <div className="text-editor-wrapper">
      <div className="text-editor-toolbar">
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={(e) => {
              e.preventDefault();
              textareaRef.current?.focus();
              applyFormat('bold');
            }}
            title="Pogrubienie"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={(e) => {
              e.preventDefault();
              textareaRef.current?.focus();
              applyFormat('italic');
            }}
            title="Kursywa"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={(e) => {
              e.preventDefault();
              textareaRef.current?.focus();
              applyFormat('underline');
            }}
            title="Podkreślenie"
          >
            <u>U</u>
          </button>
        </div>
      </div>
      <div
        ref={textareaRef}
        contentEditable
        className="form-textarea content-editable"
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
        style={{
          fontFamily: 'Arial, sans-serif',
          fontSize: '14px',
          lineHeight: '1.5',
          minHeight: `${rows * 1.5}em`,
          padding: '8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          outline: 'none',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}
      />
    </div>
  );
}

function AddAlertForm({ user, projects: initialProjects = [], users: allUsers = [], onSuccess, onCancel }) {
  const [projects, setProjects] = useState(initialProjects);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const notificationDropdownRef = useRef(null);
  const defaultNotify = useMemo(getDefaultNotificationDateTime, []);
  const [notificationDate, setNotificationDate] = useState(defaultNotify.date);
  const [notificationTime, setNotificationTime] = useState(defaultNotify.time);

  // Zamknij rozwijaną listę powiadomień po kliknięciu poza nią
  useEffect(() => {
    if (!notificationDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(e.target)) {
        setNotificationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationDropdownOpen]);
  
  useEffect(() => {
    setProjects(initialProjects);
    const handleProjectsUpdated = (event) => {
      if (event.detail) {
        setProjects(event.detail);
      } else {
        setProjects(initialProjects);
      }
    };
    window.addEventListener('projects-updated', handleProjectsUpdated);
    return () => {
      window.removeEventListener('projects-updated', handleProjectsUpdated);
    };
  }, [initialProjects]);

  const availableProjects = useMemo(() => {
    if (!user) return [];
    if (user.role === 'owner' || user.role === 'admin') {
      return projects;
    }
    return projects.filter(project => user.projects?.includes(project));
  }, [user, projects]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'open',
    category: '',
    assignedTo: '',
    dueDate: '',
    project: '',
    errorDate: '',
    correctiveActions: ''
  });

  const [images, setImages] = useState([]);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const descriptionEditorRef = useRef(null);
  const correctiveActionsEditorRef = useRef(null);
  const [formResetKey, setFormResetKey] = useState(0);
  
  // Zapisz zawartość edytorów przed re-renderem
  const savedDescriptionRef = useRef('');
  const savedCorrectiveActionsRef = useRef('');

  const priorities = [
    { value: 'low', label: 'Niski', color: '#3b82f6' },
    { value: 'medium', label: 'Średni', color: '#f59e0b' },
    { value: 'high', label: 'Wysoki', color: '#ef4444' },
    { value: 'critical', label: 'Krytyczny', color: '#dc2626' }
  ];

  const statuses = [
    { value: 'open', label: 'Otwarty', color: '#3b82f6' },
    { value: 'resolved', label: 'Rozwiązany', color: '#10b981' },
    { value: 'closed', label: 'Zamknięty', color: '#6b7280' }
  ];

  const [categories, setCategories] = useState(() => {
    try {
      const stored = localStorage.getItem('quality_categories');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Błąd wczytywania kategorii:', error);
    }
    return [
      'Błąd produkcyjny',
      'Błąd jakościowy',
      'Niezgodność dokumentacji',
      'Problemy z surowcem',
      'Problemy z maszyną',
      'Inne'
    ];
  });

  useEffect(() => {
    localStorage.setItem('quality_categories', JSON.stringify(categories));
  }, [categories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Pobierz dane z edytorów - usuń tylko puste tagi HTML (<br>, <div><br></div>, etc.)
    const getCleanHTML = (html, fieldName = '') => {
      if (!html) {
        console.log(`🔍 [getCleanHTML ${fieldName}] HTML jest pusty/null`);
        return '';
      }
      
      const originalHTML = html;
      console.log(`🔍 [getCleanHTML ${fieldName}] Oryginalny HTML (długość: ${originalHTML.length}):`, originalHTML.substring(0, 200));
      
      // Sprawdź czy HTML zawiera tylko puste tagi (<br>, <div><br></div>, białe znaki)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = originalHTML;
      
      // Pobierz tekst bez tagów HTML
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const hasText = textContent.trim().length > 0;
      
      console.log(`🔍 [getCleanHTML ${fieldName}] TextContent (długość: ${textContent.length}):`, textContent.substring(0, 100));
      console.log(`🔍 [getCleanHTML ${fieldName}] Ma tekst:`, hasText);
      
      // Jeśli nie ma tekstu, zwróć pusty string
      if (!hasText) {
        console.log(`⚠️ [getCleanHTML ${fieldName}] Brak tekstu - zwracam pusty string`);
        return '';
      }
      
      // Jeśli jest tekst, zwróć oryginalny HTML (zachowaj formatowanie)
      const trimmedHTML = originalHTML.trim();
      console.log(`✅ [getCleanHTML ${fieldName}] Zwracam HTML z tekstem (długość: ${trimmedHTML.length})`);
      return trimmedHTML;
    };
    
    // Sprawdź czy refs są dostępne
    console.log('🔍 [handleSubmit] Sprawdzanie refs:', {
      descriptionEditorRef: !!descriptionEditorRef.current,
      correctiveActionsEditorRef: !!correctiveActionsEditorRef.current,
      descriptionEditorHTML: descriptionEditorRef.current ? descriptionEditorRef.current.innerHTML.substring(0, 100) : 'BRAK REF',
      correctiveActionsEditorHTML: correctiveActionsEditorRef.current ? correctiveActionsEditorRef.current.innerHTML.substring(0, 100) : 'BRAK REF'
    });
    
    const currentDescription = descriptionEditorRef.current 
      ? getCleanHTML(descriptionEditorRef.current.innerHTML, 'description') 
      : (formData.description || '');
    const currentCorrectiveActions = correctiveActionsEditorRef.current 
      ? getCleanHTML(correctiveActionsEditorRef.current.innerHTML, 'correctiveActions') 
      : (formData.correctiveActions || '');
    
    console.log('📝 [handleSubmit] Dane z formularza:', {
      description: currentDescription.substring(0, 100),
      correctiveActions: currentCorrectiveActions.substring(0, 100),
      descriptionLength: currentDescription.length,
      correctiveActionsLength: currentCorrectiveActions.length,
      descriptionIsEmpty: !currentDescription.trim(),
      correctiveActionsIsEmpty: !currentCorrectiveActions.trim()
    });
    
    const finalFormData = {
      ...formData,
      description: currentDescription,
      correctiveActions: currentCorrectiveActions
    };

    if (!finalFormData.title.trim()) {
      setSubmitStatus({
        type: 'error',
        message: 'Wprowadź tytuł alertu'
      });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setSubmitStatus({
          type: 'error',
          message: 'Nie jesteś zalogowany. Zaloguj się ponownie.'
        });
        setTimeout(() => setSubmitStatus(null), 5000);
        return;
      }

      const alertData = {
        project: finalFormData.project || null,
        title: finalFormData.title,
        description: finalFormData.description || null,
        status: finalFormData.status || 'open',
        priority: finalFormData.priority || 'medium',
        assignedTo: finalFormData.assignedTo || null,
        createdBy: user?.email || user?.name || authAPI.getCurrentUser()?.email || 'Unknown',
        category: finalFormData.category || null,
        dueDate: finalFormData.dueDate || null,
        errorDate: finalFormData.errorDate || null,
        correctiveActions: finalFormData.correctiveActions || null,
        images: images || [],
        notifyUserIds: selectedUserIds.length > 0 ? selectedUserIds.map(id => typeof id === 'number' ? id : parseInt(id, 10)).filter(n => !isNaN(n)) : null,
        // Wyślij datę/godzinę tylko gdy użytkownik ustawi czas w przyszłości (wtedy powiadomienie pokaże się o tej porze)
        ...(selectedUserIds.length > 0 && notificationDate && notificationTime && (() => {
          const scheduled = new Date(notificationDate + 'T' + notificationTime);
          if (scheduled > new Date()) {
            return { notifyScheduledDate: notificationDate, notifyScheduledTime: notificationTime };
          }
          return {};
        })())
      };

      console.log('📤 [AddAlertForm] Wysyłanie danych do API:', {
        description: alertData.description,
        correctiveActions: alertData.correctiveActions,
        descriptionLength: alertData.description?.length || 0,
        correctiveActionsLength: alertData.correctiveActions?.length || 0,
        notifyUserIds: alertData.notifyUserIds,
        selectedUserIds: selectedUserIds,
        selectedUserIdsLength: selectedUserIds.length
      });

      const createdAlert = await qualityAPI.create(alertData);
      
      console.log('📤 [AddAlertForm] Alert utworzony, notifyUserIds:', alertData.notifyUserIds);
      console.log('📤 [AddAlertForm] Utworzone powiadomienia:', createdAlert.notificationsCreated || 0);
      
      console.log('✅ Utworzony alert:', {
        id: createdAlert.id,
        description: createdAlert.description,
        correctiveActions: createdAlert.correctiveActions,
        descriptionLength: createdAlert.description?.length || 0,
        correctiveActionsLength: createdAlert.correctiveActions?.length || 0,
        notificationsCreated: createdAlert.notificationsCreated || 0
      });
      
      // Wyślij event z informacjami o utworzonych powiadomieniach
      window.dispatchEvent(new CustomEvent('quality-alert-created', {
        detail: {
          alertId: createdAlert.id,
          notifications: createdAlert.notifications || [],
          notificationsCreated: createdAlert.notificationsCreated || 0
        }
      }));
      window.dispatchEvent(new CustomEvent('quality-alerts-updated'));

      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'open',
        category: '',
        assignedTo: '',
        dueDate: '',
        project: '',
        errorDate: '',
        correctiveActions: ''
      });
      setImages([]);
      console.log('📤 [AddAlertForm] Resetowanie selectedUserIds (było:', selectedUserIds.length, ')');
      setSelectedUserIds([]);
      setNotificationDropdownOpen(false);
      const def = getDefaultNotificationDateTime();
      setNotificationDate(def.date);
      setNotificationTime(def.time);
      setFormResetKey(prev => prev + 1);
      
      // Wyczyść zapisane zawartości edytorów po sukcesie
      savedDescriptionRef.current = '';
      savedCorrectiveActionsRef.current = '';

      setSubmitStatus({
        type: 'success',
        message: 'Alert jakościowy został dodany pomyślnie!'
      });

      setTimeout(() => {
        setSubmitStatus(null);
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (error) {
      console.error('Błąd dodawania alertu:', error);
      setSubmitStatus({
        type: 'error',
        message: `Błąd podczas zapisywania: ${error.message}`
      });
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Zapisz zawartość edytorów przed zmianą formData (aby nie stracić danych przy re-renderze)
    if (descriptionEditorRef.current) {
      savedDescriptionRef.current = descriptionEditorRef.current.innerHTML;
    }
    if (correctiveActionsEditorRef.current) {
      savedCorrectiveActionsRef.current = correctiveActionsEditorRef.current.innerHTML;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Zapisz zawartość edytorów przed zmianą formData
  useEffect(() => {
    const saveEditorsContent = () => {
      if (descriptionEditorRef.current) {
        const currentContent = descriptionEditorRef.current.innerHTML;
        // Zapisz tylko jeśli jest treść (nie zapisuj pustych <br>)
        if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
          savedDescriptionRef.current = currentContent;
          console.log('💾 Zapisano description:', currentContent.substring(0, 50));
        }
      }
      if (correctiveActionsEditorRef.current) {
        const currentContent = correctiveActionsEditorRef.current.innerHTML;
        // Zapisz tylko jeśli jest treść (nie zapisuj pustych <br>)
        if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
          savedCorrectiveActionsRef.current = currentContent;
          console.log('💾 Zapisano correctiveActions:', currentContent.substring(0, 50));
        }
      }
    };
    
    // Zapisz zawartość przed każdą zmianą formData
    saveEditorsContent();
  }, [formData.project, formData.category, formData.priority, formData.status, formData.assignedTo, formData.dueDate, formData.errorDate]);
  
  // Nasłuchuj zmian w edytorach i zapisuj je na bieżąco
  useEffect(() => {
    const descriptionEditor = descriptionEditorRef.current;
    const correctiveActionsEditor = correctiveActionsEditorRef.current;
    
    const handleDescriptionInput = () => {
      if (descriptionEditor) {
        const content = descriptionEditor.innerHTML;
        if (content.trim() && content !== '<br>' && content !== '<div><br></div>') {
          savedDescriptionRef.current = content;
        }
      }
    };
    
    const handleCorrectiveActionsInput = () => {
      if (correctiveActionsEditor) {
        const content = correctiveActionsEditor.innerHTML;
        if (content.trim() && content !== '<br>' && content !== '<div><br></div>') {
          savedCorrectiveActionsRef.current = content;
        }
      }
    };
    
    if (descriptionEditor) {
      descriptionEditor.addEventListener('input', handleDescriptionInput);
      descriptionEditor.addEventListener('blur', handleDescriptionInput);
    }
    if (correctiveActionsEditor) {
      correctiveActionsEditor.addEventListener('input', handleCorrectiveActionsInput);
      correctiveActionsEditor.addEventListener('blur', handleCorrectiveActionsInput);
    }
    
    return () => {
      if (descriptionEditor) {
        descriptionEditor.removeEventListener('input', handleDescriptionInput);
        descriptionEditor.removeEventListener('blur', handleDescriptionInput);
      }
      if (correctiveActionsEditor) {
        correctiveActionsEditor.removeEventListener('input', handleCorrectiveActionsInput);
        correctiveActionsEditor.removeEventListener('blur', handleCorrectiveActionsInput);
      }
    };
  }, []);
  
  // Funkcja pomocnicza do przywracania zawartości edytorów
  const restoreEditorsContent = () => {
    setTimeout(() => {
      if (descriptionEditorRef.current && savedDescriptionRef.current) {
        const currentContent = descriptionEditorRef.current.innerHTML;
        // Przywróć tylko jeśli edytor jest pusty (zresetowany przez re-render)
        if (!currentContent.trim() || currentContent === '<br>' || currentContent === '<div><br></div>') {
          console.log('🔄 Przywracanie zawartości description:', savedDescriptionRef.current.substring(0, 50));
          descriptionEditorRef.current.innerHTML = savedDescriptionRef.current;
        }
      }
      if (correctiveActionsEditorRef.current && savedCorrectiveActionsRef.current) {
        const currentContent = correctiveActionsEditorRef.current.innerHTML;
        // Przywróć tylko jeśli edytor jest pusty (zresetowany przez re-render)
        if (!currentContent.trim() || currentContent === '<br>' || currentContent === '<div><br></div>') {
          console.log('🔄 Przywracanie zawartości correctiveActions:', savedCorrectiveActionsRef.current.substring(0, 50));
          correctiveActionsEditorRef.current.innerHTML = savedCorrectiveActionsRef.current;
        }
      }
    }, 0);
  };
  
  // Przywróć zawartość edytorów po re-renderze (jeśli zostały zresetowane)
  useEffect(() => {
    restoreEditorsContent();
  }, [formData.project, formData.category, formData.priority, formData.status, formData.assignedTo, formData.dueDate, formData.errorDate]);
  
  // Przywróć zawartość edytorów po zmianie images lub categories
  useEffect(() => {
    restoreEditorsContent();
  }, [images, categories]);

  const handleAddImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (images.length >= 5) {
        setSubmitStatus({
          type: 'error',
          message: 'Możesz dodać maksymalnie 5 zdjęć'
        });
        setTimeout(() => setSubmitStatus(null), 3000);
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setSubmitStatus({
          type: 'error',
          message: `Plik jest za duży (max 5MB): ${file.name}`
        });
        setTimeout(() => setSubmitStatus(null), 3000);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = (height * MAX_WIDTH) / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = (width * MAX_HEIGHT) / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const compressedData = canvas.toDataURL('image/jpeg', 0.7);
          
          // Zapisz zawartość edytorów przed dodaniem zdjęcia
          if (descriptionEditorRef.current) {
            const currentContent = descriptionEditorRef.current.innerHTML;
            if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
              savedDescriptionRef.current = currentContent;
            }
          }
          if (correctiveActionsEditorRef.current) {
            const currentContent = correctiveActionsEditorRef.current.innerHTML;
            if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
              savedCorrectiveActionsRef.current = currentContent;
            }
          }
          
          const newImage = {
            name: file.name,
            data: compressedData,
            size: compressedData.length,
            comment: '',
            status: null,
            arrow: null,
            printIncluded: true
          };
          setImages(prev => [...prev, newImage]);
        };
        img.onerror = () => {
          if (result.length < 500000) {
            // Zapisz zawartość edytorów przed dodaniem zdjęcia
            if (descriptionEditorRef.current) {
              const currentContent = descriptionEditorRef.current.innerHTML;
              if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
                savedDescriptionRef.current = currentContent;
              }
            }
            if (correctiveActionsEditorRef.current) {
              const currentContent = correctiveActionsEditorRef.current.innerHTML;
              if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
                savedCorrectiveActionsRef.current = currentContent;
              }
            }
            
            const newImage = {
              name: file.name,
              data: result,
              size: file.size,
              comment: '',
              status: null,
              arrow: null,
              printIncluded: true
            };
            setImages(prev => [...prev, newImage]);
          } else {
            setSubmitStatus({
              type: 'error',
              message: 'Zdjęcie jest za duże. Spróbuj zmniejszyć rozmiar przed dodaniem.'
            });
            setTimeout(() => setSubmitStatus(null), 3000);
          }
        };
        img.src = result;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const removeImage = (index) => {
    // Zapisz zawartość edytorów przed zmianą
    if (descriptionEditorRef.current) {
      const currentContent = descriptionEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedDescriptionRef.current = currentContent;
      }
    }
    if (correctiveActionsEditorRef.current) {
      const currentContent = correctiveActionsEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedCorrectiveActionsRef.current = currentContent;
      }
    }
    
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const updateImageComment = (index, comment) => {
    // Zapisz zawartość edytorów przed zmianą komentarza
    if (descriptionEditorRef.current) {
      const currentContent = descriptionEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedDescriptionRef.current = currentContent;
      }
    }
    if (correctiveActionsEditorRef.current) {
      const currentContent = correctiveActionsEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedCorrectiveActionsRef.current = currentContent;
      }
    }
    
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, comment } : img
    ));
  };

  const updateImageStatus = (index, status) => {
    // Zapisz zawartość edytorów przed zmianą statusu
    if (descriptionEditorRef.current) {
      const currentContent = descriptionEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedDescriptionRef.current = currentContent;
      }
    }
    if (correctiveActionsEditorRef.current) {
      const currentContent = correctiveActionsEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedCorrectiveActionsRef.current = currentContent;
      }
    }
    
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, status } : img
    ));
  };

  const updateImagePrintIncluded = (index, printIncluded) => {
    // Zapisz zawartość edytorów przed zmianą printIncluded
    if (descriptionEditorRef.current) {
      const currentContent = descriptionEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedDescriptionRef.current = currentContent;
      }
    }
    if (correctiveActionsEditorRef.current) {
      const currentContent = correctiveActionsEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedCorrectiveActionsRef.current = currentContent;
      }
    }
    
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, printIncluded } : img
    ));
  };

  const handleImageClick = (index, event) => {
    const imgElement = event.currentTarget.querySelector('img');
    if (!imgElement) return;
    
    // Zapisz zawartość edytorów przed zmianą strzałki
    if (descriptionEditorRef.current) {
      const currentContent = descriptionEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedDescriptionRef.current = currentContent;
      }
    }
    if (correctiveActionsEditorRef.current) {
      const currentContent = correctiveActionsEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedCorrectiveActionsRef.current = currentContent;
      }
    }
    
    const rect = imgElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    const currentImage = images[index];
    if (currentImage.arrow) {
      const arrowX = currentImage.arrow.x;
      const arrowY = currentImage.arrow.y;
      const distance = Math.sqrt(Math.pow(x - arrowX, 2) + Math.pow(y - arrowY, 2));
      
      if (distance < 5) {
        setImages(prev => prev.map((img, i) => 
          i === index ? { ...img, arrow: null } : img
        ));
        return;
      }
    }
    
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, arrow: { x, y } } : img
    ));
  };

  const removeImageArrow = (index, event) => {
    event.stopPropagation();
    
    // Zapisz zawartość edytorów przed usunięciem strzałki
    if (descriptionEditorRef.current) {
      const currentContent = descriptionEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedDescriptionRef.current = currentContent;
      }
    }
    if (correctiveActionsEditorRef.current) {
      const currentContent = correctiveActionsEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedCorrectiveActionsRef.current = currentContent;
      }
    }
    
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, arrow: null } : img
    ));
  };

  const addCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      // Zapisz zawartość edytorów przed dodaniem kategorii
      if (descriptionEditorRef.current) {
        const currentContent = descriptionEditorRef.current.innerHTML;
        if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
          savedDescriptionRef.current = currentContent;
        }
      }
      if (correctiveActionsEditorRef.current) {
        const currentContent = correctiveActionsEditorRef.current.innerHTML;
        if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
          savedCorrectiveActionsRef.current = currentContent;
        }
      }
      
      setCategories(prev => [...prev, newCategory.trim()]);
      setNewCategory('');
      setShowAddCategory(false);
    }
  };

  const removeCategory = (categoryToRemove) => {
    // Zapisz zawartość edytorów przed usunięciem kategorii
    if (descriptionEditorRef.current) {
      const currentContent = descriptionEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedDescriptionRef.current = currentContent;
      }
    }
    if (correctiveActionsEditorRef.current) {
      const currentContent = correctiveActionsEditorRef.current.innerHTML;
      if (currentContent.trim() && currentContent !== '<br>' && currentContent !== '<div><br></div>') {
        savedCorrectiveActionsRef.current = currentContent;
      }
    }
    
    if (categoryToRemove === formData.category) {
      setFormData(prev => ({ ...prev, category: '' }));
    }
    setCategories(prev => prev.filter(cat => cat !== categoryToRemove));
  };

  return (
    <div className="add-alert-container">
      <div className="add-alert-header">
        <p className="add-alert-subtitle">Wypełnij formularz, aby utworzyć nowy alert jakościowy</p>
      </div>
      
      <form onSubmit={handleSubmit} className="quality-form">
        {/* Sekcja: Podstawowe informacje */}
        <div className="form-section">
          <h4 className="form-section-title">Podstawowe informacje</h4>
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              Tytuł alertu: <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="form-input"
              placeholder="np. Błąd w procesie produkcyjnym"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Opis:
            </label>
            <AddAlertTextEditor
              placeholder="Szczegółowy opis problemu..."
              textareaRef={descriptionEditorRef}
              rows={5}
              resetKey={formResetKey}
            />
          </div>
        </div>

        {/* Sekcja: Szczegóły */}
        <div className="form-section">
          <h4 className="form-section-title">Szczegóły</h4>
          <div className="form-row-three">
            <div className="form-group">
              <label htmlFor="priority" className="form-label">
                Priorytet:
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="form-select"
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status:
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="project" className="form-label">
                Klient: <span className="required-asterisk">*</span>
              </label>
              <select
                id="project"
                name="project"
                value={formData.project}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">-- Wybierz klienta --</option>
                {availableProjects.map(project => (
                  <option key={project} value={project}>
                    {project}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="category" className="form-label">
              Kategoria:
            </label>
            <div className="category-select-wrapper">
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">-- Wybierz kategorię --</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <div className="category-actions">
                <button
                  type="button"
                  className="btn-add-category"
                  onClick={() => setShowAddCategory(!showAddCategory)}
                  title="Dodaj kategorię"
                >
                  +
                </button>
              </div>
            </div>
            {showAddCategory && (
              <div className="add-category-form">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="form-input"
                  placeholder="Nazwa nowej kategorii"
                  onKeyPress={(e) => e.key === 'Enter' && addCategory()}
                />
                <button
                  type="button"
                  className="btn-confirm-category"
                  onClick={addCategory}
                >
                  Dodaj
                </button>
                <button
                  type="button"
                  className="btn-cancel-category"
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategory('');
                  }}
                >
                  Anuluj
                </button>
              </div>
            )}
            <div className="categories-list">
              {categories.map(category => (
                <span key={category} className="category-tag">
                  {category}
                  <button
                    type="button"
                    className="btn-remove-category"
                    onClick={() => removeCategory(category)}
                    title="Usuń kategorię"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-row-three">
            <div className="form-group">
              <label htmlFor="assignedTo" className="form-label">
                Przypisany do:
              </label>
              <input
                type="text"
                id="assignedTo"
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                className="form-input"
                placeholder="Email lub nazwa użytkownika"
              />
            </div>

            <div className="form-group">
              <label htmlFor="errorDate" className="form-label">
                Data powstania błędu:
              </label>
              <input
                type="date"
                id="errorDate"
                name="errorDate"
                value={formData.errorDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dueDate" className="form-label">
                Termin rozwiązania:
              </label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Sekcja: Kroki zaradcze */}
        <div className="form-section">
          <h4 className="form-section-title">Kroki zaradcze</h4>
          <div className="form-group">
            <label htmlFor="correctiveActions" className="form-label">
              Opisz kroki, które należy podjąć, aby naprawić błąd:
            </label>
            <AddAlertTextEditor
              placeholder="Opisz kroki, które należy podjąć, aby naprawić błąd..."
              textareaRef={correctiveActionsEditorRef}
              rows={4}
              resetKey={formResetKey}
            />
          </div>
        </div>

        {/* Sekcja: Powiadomienia */}
        <div className="form-section">
          <h4 className="form-section-title">Powiadomienia</h4>
          <div className="form-group">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '16px' }}>
              <div ref={notificationDropdownRef} style={{ minWidth: '220px', flex: '1 1 220px', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>
                  Wybierz osoby, które otrzymają powiadomienie o nowym alercie:
                </label>
                <button
                  type="button"
                  onClick={() => setNotificationDropdownOpen(prev => !prev)}
                  style={{
                    width: '100%',
                    height: '38px',
                    minHeight: '38px',
                    padding: '0 12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    background: 'white',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    boxSizing: 'border-box'
                  }}
                  aria-expanded={notificationDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span style={{ color: selectedUserIds.length ? '#111' : '#6b7280' }}>
                    {selectedUserIds.length === 0
                      ? 'Wybierz osoby...'
                      : `Wybrano ${selectedUserIds.length} ${selectedUserIds.length === 1 ? 'osobę' : 'osób'}`}
                  </span>
                  <span style={{ flexShrink: 0 }}>{notificationDropdownOpen ? '▲' : '▼'}</span>
                </button>
                {notificationDropdownOpen && (
                  <div
                    role="listbox"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      maxHeight: '240px',
                      overflowY: 'auto',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      background: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      padding: '8px 0'
                    }}
                  >
                    {allUsers.filter(u => u.role !== 'employee').length === 0 ? (
                      <div style={{ padding: '12px', fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
                        Brak dostępnych użytkowników
                      </div>
                    ) : (
                      allUsers.filter(u => u.role !== 'employee').map(usr => {
                        const id = typeof usr.id === 'number' ? usr.id : parseInt(usr.id, 10);
                        const checked = selectedUserIds.includes(id);
                        return (
                          <label
                            key={usr.id}
                            role="option"
                            aria-selected={checked}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              margin: 0,
                              justifyContent: 'space-between'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span style={{ fontSize: '14px', color: '#333', flex: 1 }}>
                              {usr.name} ({usr.email}) {usr.role === 'owner' || usr.role === 'admin' ? `[${usr.role}]` : (usr.role ? `[${usr.role}]` : '')}
                            </span>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setSelectedUserIds(prev =>
                                  checked ? prev.filter(uid => uid !== id) : [...prev, id]
                                );
                              }}
                              style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                            />
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <div style={{ minWidth: '140px', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>
                  Data powiadomienia:
                </label>
                <input
                  type="date"
                  value={notificationDate}
                  onChange={(e) => setNotificationDate(e.target.value)}
                  className="form-input"
                  style={{ width: '100%', height: '38px', padding: '0 12px', border: '2px solid #e0e0e0', borderRadius: '8px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ minWidth: '120px', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>
                  Godzina powiadomienia:
                </label>
                <input
                  type="time"
                  value={notificationTime}
                  onChange={(e) => setNotificationTime(e.target.value)}
                  className="form-input"
                  style={{ width: '100%', height: '38px', padding: '0 12px', border: '2px solid #e0e0e0', borderRadius: '8px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
              Puste = od razu. Wpisz godzinę, aby zaplanować. Powiadomienie będzie miało odnośnik do tej karty jakościowej.
            </p>
          </div>
        </div>

        {/* Sekcja: Zdjęcia */}
        <div className="form-section">
          <h4 className="form-section-title">Zdjęcia</h4>
          <p className="form-section-hint">Maksymalnie 5 zdjęć, każde do 5MB</p>
          <div className="images-section">
            {images.length < 5 && (
              <button
                type="button"
                className="btn-add-image"
                onClick={handleAddImage}
                title="Dodaj zdjęcie"
              >
                +
              </button>
            )}
            {images.length > 0 && (
              <div className="images-preview">
                <div className="images-grid">
                  {images.map((image, index) => (
                    <div key={index} className="image-preview-item">
                      <div 
                        className="image-wrapper"
                        onClick={(e) => handleImageClick(index, e)}
                        style={{ cursor: 'crosshair', position: 'relative' }}
                      >
                        <img src={image.data} alt={image.name} className="preview-image" />
                        {image.status && (
                          <div className={`image-status-overlay ${image.status.toLowerCase()}`}></div>
                        )}
                        {image.arrow && (
                          <div 
                            className="image-arrow"
                            style={{
                              position: 'absolute',
                              left: `${image.arrow.x}%`,
                              top: `${image.arrow.y}%`,
                              transform: 'translate(-50%, -100%)',
                              pointerEvents: 'none',
                              zIndex: 4
                            }}
                          >
                            <svg width="30" height="30" viewBox="0 0 40 40">
                              <path
                                d="M 20 0 L 0 30 L 15 30 L 15 40 L 25 40 L 25 30 L 40 30 Z"
                                fill="#FFE066"
                                stroke="#FFB84D"
                                strokeWidth="1.5"
                                opacity="0.9"
                              />
                            </svg>
                          </div>
                        )}
                        {image.arrow && (
                          <button
                            type="button"
                            className="remove-arrow-btn"
                            onClick={(e) => removeImageArrow(index, e)}
                            title="Usuń strzałkę"
                          >
                            ×
                          </button>
                        )}
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeImage(index)}
                          title="Usuń zdjęcie"
                        >
                          ×
                        </button>
                      </div>
                      <div className="image-arrow-hint">
                        {image.arrow ? 'Kliknij na strzałkę, aby ją usunąć' : 'Kliknij na zdjęcie, aby dodać strzałkę wskazującą wadę'}
                      </div>
                      <input
                        type="text"
                        value={image.comment || ''}
                        onChange={(e) => updateImageComment(index, e.target.value)}
                        className="image-comment-input"
                        placeholder="Dodaj komentarz..."
                      />
                      <div className="image-status-buttons">
                        <button
                          type="button"
                          className={`btn-status ${image.status === 'NOK' ? 'active nok' : 'nok'}`}
                          onClick={() => updateImageStatus(index, image.status === 'NOK' ? null : 'NOK')}
                        >
                          NOK
                        </button>
                        <button
                          type="button"
                          className={`btn-status ${image.status === 'OK' ? 'active ok' : 'ok'}`}
                          onClick={() => updateImageStatus(index, image.status === 'OK' ? null : 'OK')}
                        >
                          OK
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                          <input
                            type="checkbox"
                            checked={image.printIncluded !== false}
                            onChange={(e) => updateImagePrintIncluded(index, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: 'pointer', width: '16px', height: '16px', margin: 0 }}
                          />
                          <span>Drukuj na karcie</span>
                        </label>
                      </div>
                      <span className="image-name">{image.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {submitStatus && (
          <div className={`submit-status ${submitStatus.type}`}>
            {submitStatus.type === 'success' && '✓ '}
            {submitStatus.type === 'error' && '✗ '}
            {submitStatus.message}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn-submit">
            Dodaj Alert
          </button>
          <button
            type="button"
            className="btn-reset"
            onClick={() => {
              setFormData({
                title: '',
                description: '',
                priority: 'medium',
                status: 'open',
                category: '',
                assignedTo: '',
                dueDate: '',
                project: '',
                errorDate: '',
                correctiveActions: ''
              });
              setImages([]);
              setSubmitStatus(null);
            }}
          >
            Wyczyść
          </button>
          <button
            type="button"
            className="btn-reset"
            onClick={onCancel}
          >
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}

export default Tablica;
